import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { uploadVetrineImage } from '../../services/storageService';

const AdminInsertVetrina = () => {
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'not_logged' | 'not_admin' | 'admin'
  const [userInfo, setUserInfo] = useState({ id: null, email: null, role: null });
  const [categories, setCategories] = useState([]);
  const [zones, setZones] = useState([]);
  const [formData, setFormData] = useState({
    nome_negozio: '',
    categoria: '',
    zona: '',
    indirizzo: '',
    telefono: '',
    whatsapp: '',
    email: '',
    facebook: '',
    instagram: '',
    descrizione_breve: '',
    sito_web: '',
    orari_apertura: '',
    logo_negozio: '',
    immagine_principale: '',
    gallery_prodotti: [],
    tipo_vetrina: 'reale',
    platform_target: 'cliccami'
  });
  const [imageFiles, setImageFiles] = useState({
    logo: null,
    mainImage: null,
    gallery: []
  });
  const [uploadProgress, setUploadProgress] = useState({
    logo: false,
    mainImage: false,
    gallery: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // { success: boolean, message: string, insertedId?: string }

  // Check authentication and role
  useEffect(() => {
    const checkAuthAndRole = async () => {
      try {
        // Get session
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        
        if (sessionError || !session?.user) {
          setAuthState('not_logged');
          setUserInfo({ id: null, email: null, role: null });
          return;
        }

        const userId = session?.user?.id;
        const userEmail = session?.user?.email;

        // Query role from public.user_profiles
        const { data: profileData, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('role')
          ?.eq('id', userId)
          ?.single();

        if (profileError || !profileData) {
          setAuthState('not_admin');
          setUserInfo({ id: userId, email: userEmail, role: 'error' });
          return;
        }

        const userRole = profileData?.role;
        setUserInfo({ id: userId, email: userEmail, role: userRole });

        if (userRole === 'admin') {
          setAuthState('admin');
        } else {
          setAuthState('not_admin');
        }
      } catch (err) {
        console.error('Error checking auth:', err);
        setAuthState('not_admin');
        setUserInfo({ id: null, email: null, role: 'error' });
      }
    };

    checkAuthAndRole();
  }, []);

  // Load categories and zones when admin
  useEffect(() => {
    if (authState === 'admin') {
      loadCategories();
      loadZones();
    }
  }, [authState]);

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase?.from('vetrine_categories')?.select('*')?.order('label');
      if (error) {
        console.error('Error loading categories:', error);
      } else {
        setCategories(data || []);
      }
    } catch (err) {
      console.error('Exception loading categories:', err);
    }
  };

  const loadZones = async () => {
    try {
      const { data, error } = await supabase?.from('vetrine_zones')?.select('*')?.order('label');
      if (error) {
        console.error('Error loading zones:', error);
      } else {
        setZones(data || []);
      }
    } catch (err) {
      console.error('Exception loading zones:', err);
    }
  };

  const handleImageSelect = (e, type) => {
    const files = e?.target?.files;
    if (!files || files?.length === 0) return;

    if (type === 'gallery') {
      // Multiple files for gallery
      const fileArray = Array.from(files);
      setImageFiles(prev => ({ ...prev, gallery: [...prev?.gallery, ...fileArray] }));
    } else {
      // Single file for logo or main image
      setImageFiles(prev => ({ ...prev, [type]: files?.[0] }));
    }
  };

  const removeGalleryImage = (index) => {
    setImageFiles(prev => ({
      ...prev,
      gallery: prev?.gallery?.filter((_, i) => i !== index)
    }));
  };

  const uploadImages = async () => {
    const uploadedUrls = {
      logo: '',
      mainImage: '',
      gallery: []
    };

    try {
      // Upload logo
      if (imageFiles?.logo) {
        setUploadProgress(prev => ({ ...prev, logo: true }));
        const result = await uploadVetrineImage(imageFiles?.logo, 'logos');
        if (result?.success) {
          uploadedUrls.logo = result?.publicUrl;
        } else {
          throw new Error(`Errore upload logo: ${result.error}`);
        }
        setUploadProgress(prev => ({ ...prev, logo: false }));
      }

      // Upload main image
      if (imageFiles?.mainImage) {
        setUploadProgress(prev => ({ ...prev, mainImage: true }));
        const result = await uploadVetrineImage(imageFiles?.mainImage, 'main');
        if (result?.success) {
          uploadedUrls.mainImage = result?.publicUrl;
        } else {
          throw new Error(`Errore upload immagine principale: ${result.error}`);
        }
        setUploadProgress(prev => ({ ...prev, mainImage: false }));
      }

      // Upload gallery images
      if (imageFiles?.gallery?.length > 0) {
        setUploadProgress(prev => ({ ...prev, gallery: true }));
        for (const file of imageFiles?.gallery) {
          const result = await uploadVetrineImage(file, 'gallery');
          if (result?.success) {
            uploadedUrls?.gallery?.push(result?.publicUrl);
          } else {
            console.error(`Errore upload galleria: ${result?.error}`);
          }
        }
        setUploadProgress(prev => ({ ...prev, gallery: false }));
      }

      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      // Upload images first
      const uploadedUrls = await uploadImages();

      // Insert into database with image URLs
      const { data, error } = await supabase
        ?.from('vetrine_stores')
        ?.insert({
          nome_negozio: formData?.nome_negozio,
          categoria: formData?.categoria,
          zona: formData?.zona,
          indirizzo: formData?.indirizzo,
          telefono: formData?.telefono,
          whatsapp: formData?.whatsapp || null,
          email: formData?.email || null,
          facebook: formData?.facebook || null,
          instagram: formData?.instagram || null,
          descrizione_breve: formData?.descrizione_breve || null,
          sito_web: formData?.sito_web || null,
          orari_apertura: formData?.orari_apertura || null,
          logo_negozio: uploadedUrls?.logo || null,
          immagine_principale: uploadedUrls?.mainImage || null,
          gallery_prodotti: uploadedUrls?.gallery?.length > 0 ? uploadedUrls?.gallery : null,
          tipo_vetrina: formData?.tipo_vetrina || 'reale',
          platform_target: formData?.platform_target || 'cliccami',
          status: 'attiva',
          created_by_admin: userInfo?.id,
          updated_by_admin: userInfo?.id
        })
        ?.select()
        ?.single();

      if (error) {
        setSubmitResult({
          success: false,
          message: `Errore: ${error?.message}`
        });
      } else {
        setSubmitResult({
          success: true,
          message: 'Inserimento riuscito!',
          insertedId: data?.id
        });
        // Reset form
        setFormData({
          nome_negozio: '',
          categoria: '',
          zona: '',
          indirizzo: '',
          telefono: '',
          whatsapp: '',
          email: '',
          facebook: '',
          instagram: '',
          descrizione_breve: '',
          sito_web: '',
          orari_apertura: '',
          logo_negozio: '',
          immagine_principale: '',
          gallery_prodotti: [],
          tipo_vetrina: 'reale',
          platform_target: 'cliccami'
        });
        setImageFiles({
          logo: null,
          mainImage: null,
          gallery: []
        });
      }
    } catch (err) {
      console.error('Exception during insert:', err);
      setSubmitResult({
        success: false,
        message: `Errore: ${err?.message}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (authState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-700">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* User Info Panel - Always Visible */}
        <div className="bg-blue-900 text-white p-4 rounded-lg mb-6 shadow-lg">
          <h3 className="text-lg font-bold mb-2">Informazioni Utente</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm font-mono">
            <div>
              <span className="opacity-75">ID:</span>
              <div className="font-semibold break-all">{userInfo?.id || 'N/A'}</div>
            </div>
            <div>
              <span className="opacity-75">Email:</span>
              <div className="font-semibold">{userInfo?.email || 'N/A'}</div>
            </div>
            <div>
              <span className="opacity-75">Role:</span>
              <div className="font-semibold uppercase">{userInfo?.role || 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Not Logged In State */}
        {authState === 'not_logged' && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Non sei autenticato</h2>
            <p className="text-gray-600 mb-6">Devi effettuare il login come amministratore per accedere a questa pagina.</p>
            <Link
              to="/admin-login"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Vai a /admin-login
            </Link>
          </div>
        )}

        {/* Not Admin State */}
        {authState === 'not_admin' && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-6xl mb-4">⛔</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Non autorizzato</h2>
            <p className="text-gray-600 mb-6">Non hai i permessi necessari per accedere a questa pagina. Solo gli amministratori possono inserire vetrine.</p>
            <Link
              to="/public-vetrine"
              className="inline-block bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Vai a /public-vetrine
            </Link>
          </div>
        )}

        {/* Admin Form State */}
        {authState === 'admin' && (
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-gray-800">Inserimento Vetrina (Admin)</h1>
              <Link
                to="/admin-vetrine"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors shadow-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Gestisci Vetrine Esistenti
              </Link>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Piattaforma */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Piattaforma <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData?.platform_target}
                  onChange={(e) => setFormData({ ...formData, platform_target: e?.target?.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="cliccami">cliccami</option>
                  <option value="unidaymc">unidaymc</option>
                  <option value="both">both</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Seleziona la piattaforma di destinazione per questa vetrina</p>
              </div>

              {/* Nome Negozio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome Negozio *
                </label>
                <input
                  type="text"
                  value={formData?.nome_negozio}
                  onChange={(e) => setFormData({ ...formData, nome_negozio: e?.target?.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: Pizzeria Da Mario"
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  value={formData?.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e?.target?.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleziona categoria</option>
                  {categories?.map((cat) => (
                    <option key={cat?.id} value={cat?.value}>
                      {cat?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Zona */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Zona *
                </label>
                <select
                  value={formData?.zona}
                  onChange={(e) => setFormData({ ...formData, zona: e?.target?.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleziona zona</option>
                  {zones?.map((zone) => (
                    <option key={zone?.id} value={zone?.value}>
                      {zone?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Indirizzo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Indirizzo *
                </label>
                <input
                  type="text"
                  value={formData?.indirizzo}
                  onChange={(e) => setFormData({ ...formData, indirizzo: e?.target?.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: Via Roma 123, Macerata"
                />
              </div>

              {/* Telefono */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefono *
                </label>
                <input
                  type="tel"
                  value={formData?.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e?.target?.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: 0733123456"
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefono WhatsApp Business
                </label>
                <input
                  type="tel"
                  value={formData?.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: 3331234567"
                />
                <p className="text-xs text-gray-500 mt-1">Numero WhatsApp Business del negozio (opzionale)</p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@negozio.it"
                />
                <p className="text-xs text-gray-500 mt-1">Email di contatto del negozio</p>
              </div>

              {/* Tipo Vetrina */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Vetrina <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="tipo_vetrina"
                      value="reale"
                      checked={formData?.tipo_vetrina === 'reale'}
                      onChange={(e) => setFormData({ ...formData, tipo_vetrina: e?.target?.value })}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      <span className="font-semibold">Vetrina Reale</span>
                      <span className="block text-xs text-gray-500">Negozio confermato</span>
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="tipo_vetrina"
                      value="demo"
                      checked={formData?.tipo_vetrina === 'demo'}
                      onChange={(e) => setFormData({ ...formData, tipo_vetrina: e?.target?.value })}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">
                      <span className="font-semibold">Vetrina Demo</span>
                      <span className="block text-xs text-gray-500">Esempio per riunioni</span>
                    </span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Le vetrine demo servono come esempi durante le riunioni con i clienti. Le vetrine reali sono negozi confermati.
                </p>
              </div>

              {/* Descrizione Breve */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Descrizione Breve
                </label>
                <textarea
                  value={formData?.descrizione_breve}
                  onChange={(e) => setFormData({ ...formData, descrizione_breve: e?.target?.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: Pizzeria tradizionale con forno a legna, specialità napoletane"
                />
                <p className="text-xs text-gray-500 mt-1">Breve descrizione del negozio (max 200 caratteri)</p>
              </div>

              {/* Sito Web */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sito Web
                </label>
                <input
                  type="url"
                  value={formData?.sito_web}
                  onChange={(e) => setFormData({ ...formData, sito_web: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: https://www.nomenegozio.it"
                />
                <p className="text-xs text-gray-500 mt-1">URL completo del sito web (includi https://)</p>
              </div>

              {/* Orari Apertura */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Orari Apertura
                </label>
                <textarea
                  value={formData?.orari_apertura}
                  onChange={(e) => setFormData({ ...formData, orari_apertura: e?.target?.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: Lun-Ven: 9:00-13:00, 16:00-20:00&#10;Sabato: 9:00-13:00&#10;Domenica: Chiuso"
                />
                <p className="text-xs text-gray-500 mt-1">Orari di apertura settimanali del negozio</p>
              </div>

              {/* Facebook */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Facebook
                </label>
                <input
                  type="text"
                  value={formData?.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: @nomenegozio o https://facebook.com/nomenegozio"
                />
                <p className="text-xs text-gray-500 mt-1">Inserisci username (@nomenegozio) o link completo</p>
              </div>

              {/* Instagram */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Instagram
                </label>
                <input
                  type="text"
                  value={formData?.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es: @nomenegozio o https://instagram.com/nomenegozio"
                />
                <p className="text-xs text-gray-500 mt-1">Inserisci username (@nomenegozio) o link completo</p>
              </div>

              {/* Logo Negozio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Logo Negozio
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => handleImageSelect(e, 'logo')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {imageFiles?.logo && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    ✓ File selezionato: {imageFiles?.logo?.name}
                  </div>
                )}
                {uploadProgress?.logo && (
                  <div className="mt-2 text-sm text-blue-600">⏳ Caricamento in corso...</div>
                )}
                <p className="text-xs text-gray-500 mt-1">Formati: JPG, PNG, WebP - Max 10MB</p>
              </div>

              {/* Immagine Principale Vetrina */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Immagine Vetrina Principale
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => handleImageSelect(e, 'mainImage')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {imageFiles?.mainImage && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    ✓ File selezionato: {imageFiles?.mainImage?.name}
                  </div>
                )}
                {uploadProgress?.mainImage && (
                  <div className="mt-2 text-sm text-blue-600">⏳ Caricamento in corso...</div>
                )}
                <p className="text-xs text-gray-500 mt-1">Immagine principale della vetrina - Max 10MB</p>
              </div>

              {/* Galleria Prodotti */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Galleria Prodotti
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={(e) => handleImageSelect(e, 'gallery')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {imageFiles?.gallery?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {imageFiles?.gallery?.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                        <span>✓ {file?.name}</span>
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="text-red-600 hover:text-red-800 font-semibold"
                        >
                          Rimuovi
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {uploadProgress?.gallery && (
                  <div className="mt-2 text-sm text-blue-600">⏳ Caricamento galleria in corso...</div>
                )}
                <p className="text-xs text-gray-500 mt-1">Seleziona più immagini per la galleria prodotti - Max 10MB ciascuna</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed' :'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Invio in corso...' : 'INVIA'}
              </button>
            </form>

            {/* Submit Result */}
            {submitResult && (
              <div
                className={`mt-6 p-4 rounded-lg ${
                  submitResult?.success
                    ? 'bg-green-100 border border-green-400 text-green-800' :'bg-red-100 border border-red-400 text-red-800'
                }`}
              >
                <div className="font-semibold text-lg mb-2">
                  {submitResult?.success ? '✅ Successo' : '❌ Errore'}
                </div>
                <div className="mb-2">{submitResult?.message}</div>
                {submitResult?.insertedId && (
                  <div className="mt-3 p-3 bg-white rounded border border-green-300">
                    <span className="font-semibold">ID riga inserita:</span>
                    <div className="font-mono text-sm mt-1 break-all">{submitResult?.insertedId}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInsertVetrina;